import React from 'react';
import { blockedAppsAPI, orgAPI } from '../services/api';
import ScopeBadge from '../components/ScopeBadge';
import EmployeePicker from '../components/EmployeePicker';
import {
    Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
    Skeleton, Alert, Stack, TextField, Select, MenuItem, FormControl,
    InputLabel, Pagination, Switch, IconButton, Button, InputAdornment, Grid,
} from '@mui/material';
import { SearchRounded, DeleteOutlineRounded, AddRounded } from '@mui/icons-material';

const ALL = '';

export default function AppBlocking() {
    const [search, setSearch]     = React.useState('');
    const [type, setType]         = React.useState(ALL);
    const [page, setPage]         = React.useState(1);
    const [records, setRecords]   = React.useState([]);
    const [pagination, setPagination] = React.useState({ total: 0, pages: 1 });
    const [loading, setLoading]   = React.useState(true);
    const [error, setError]       = React.useState('');

    // Add-rule form
    const [newAppName, setNewAppName] = React.useState('');
    const [newType, setNewType]       = React.useState('employee');
    const [units, setUnits]           = React.useState([]);
    const [teams, setTeams]           = React.useState([]);
    const [newUnitId, setNewUnitId]   = React.useState('');
    const [newTeamId, setNewTeamId]   = React.useState('');
    const [newEmployee, setNewEmployee] = React.useState(null);
    const [adding, setAdding]         = React.useState(false);

    React.useEffect(() => { setPage(1); }, [search, type]);

    // The Unit/Team pickers below only offer the caller's own units/teams
    // (the backend enforces this too — see authorizeScopeTarget — but the
    // UI shouldn't offer choices that would just get rejected).
    React.useEffect(() => {
        orgAPI.getUnits('settings.productivity_rating').then((res) => { if (res.data.error !== '1') setUnits(res.data.units || []); }).catch(() => {});
    }, []);

    React.useEffect(() => {
        orgAPI.getTeams(newUnitId || undefined, 'settings.productivity_rating').then((res) => { if (res.data.error !== '1') setTeams(res.data.teams || []); }).catch(() => {});
        setNewTeamId('');
    }, [newUnitId]);

    const load = React.useCallback(() => {
        setLoading(true);
        setError('');
        blockedAppsAPI.list({ search: search || undefined, type: type || undefined, page })
            .then((res) => {
                if (res.data.error === '1') throw new Error(res.data.message || 'Failed to load blocked apps');
                setRecords(res.data.records || []);
                setPagination(res.data.pagination || { total: 0, pages: 1 });
            })
            .catch((err) => setError(err.message || 'Failed to load blocked apps'))
            .finally(() => setLoading(false));
    }, [search, type, page]);

    React.useEffect(() => { load(); }, [load]);

    const handleToggle = async (row) => {
        try {
            await blockedAppsAPI.save({ app_name: row.app_name, type: row.type, scope_id: row.scope_id, is_blocked: !row.is_blocked });
            setRecords((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_blocked: !r.is_blocked } : r)));
        } catch (err) {
            setError('Failed to update rule. Please try again.');
        }
    };

    const handleDelete = async (row) => {
        try {
            await blockedAppsAPI.remove(row.id);
            load();
        } catch (err) {
            setError('Failed to delete rule. Please try again.');
        }
    };

    const canAdd = newAppName.trim() && (
        (newType === 'employee' && newEmployee) ||
        (newType === 'team' && newTeamId) ||
        (newType === 'unit' && newUnitId)
    );

    const handleAdd = async () => {
        if (!canAdd) return;
        setAdding(true);
        setError('');
        try {
            const scope_id = newType === 'employee' ? newEmployee.user_id : newType === 'team' ? newTeamId : newUnitId;
            await blockedAppsAPI.save({ app_name: newAppName.trim(), type: newType, scope_id, is_blocked: true });
            setNewAppName(''); setNewEmployee(null); setNewTeamId(''); setNewUnitId('');
            load();
        } catch (err) {
            setError('Failed to add rule. Please try again.');
        } finally {
            setAdding(false);
        }
    };

    return (
        <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h2" sx={{ mb: 0.5 }}>App Blocking</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1">
                            {loading ? 'Loading…' : `${pagination.total} block ${pagination.total === 1 ? 'rule' : 'rules'}`}
                        </Typography>
                        <ScopeBadge featureKey="settings.productivity_rating" />
                    </Stack>
                </Box>
                <TextField
                    size="small" placeholder="Search app or domain…" value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 260 } }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment> }}
                />
            </Stack>

            {/* Add a new rule */}
            <Paper sx={{ p: 2.5, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Block an app or site</Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth label="App name or domain" value={newAppName} onChange={(e) => setNewAppName(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Scope</InputLabel>
                            <Select label="Scope" value={newType} onChange={(e) => setNewType(e.target.value)}>
                                <MenuItem value="employee">Employee</MenuItem>
                                <MenuItem value="team">Team</MenuItem>
                                <MenuItem value="unit">Unit</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        {newType === 'employee' && <EmployeePicker value={newEmployee} onChange={setNewEmployee} sx={{ width: '100%' }} />}
                        {newType === 'team' && (
                            <Stack direction="row" spacing={1}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Unit</InputLabel>
                                    <Select label="Unit" value={newUnitId} onChange={(e) => setNewUnitId(e.target.value)}>
                                        <MenuItem value="">All Units</MenuItem>
                                        {units.map((u) => <MenuItem key={u.unit_id} value={u.unit_id}>{u.unit_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Team</InputLabel>
                                    <Select label="Team" value={newTeamId} onChange={(e) => setNewTeamId(e.target.value)}>
                                        <MenuItem value="">Select…</MenuItem>
                                        {teams.map((t) => <MenuItem key={t.team_id} value={t.team_id}>{t.team_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Stack>
                        )}
                        {newType === 'unit' && (
                            <FormControl size="small" fullWidth>
                                <InputLabel>Unit</InputLabel>
                                <Select label="Unit" value={newUnitId} onChange={(e) => setNewUnitId(e.target.value)}>
                                    <MenuItem value="">Select…</MenuItem>
                                    {units.map((u) => <MenuItem key={u.unit_id} value={u.unit_id}>{u.unit_name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Button fullWidth variant="contained" startIcon={<AddRounded />} disabled={!canAdd || adding} onClick={handleAdd}>
                            {adding ? 'Adding…' : 'Block It'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Scope Type</InputLabel>
                    <Select label="Scope Type" value={type} onChange={(e) => setType(e.target.value)}>
                        <MenuItem value={ALL}>All Scopes</MenuItem>
                        <MenuItem value="employee">Employee</MenuItem>
                        <MenuItem value="team">Team</MenuItem>
                        <MenuItem value="unit">Unit</MenuItem>
                    </Select>
                </FormControl>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

            <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>App / Domain</TableCell>
                                <TableCell>Scope</TableCell>
                                <TableCell>Blocked</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={4}>
                                            <Skeleton variant="rectangular" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1 }} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : records.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">No block rules yet.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                records.map((r) => (
                                    <TableRow key={r.id} sx={{ '&:last-child td': { border: 0 } }}>
                                        <TableCell>{r.app_name}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{r.scope_name}</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{r.type}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Switch checked={r.is_blocked} onChange={() => handleToggle(r)} color="error" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => handleDelete(r)}>
                                                <DeleteOutlineRounded fontSize="small" sx={{ color: 'text.secondary' }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Box>
                {pagination.pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        <Pagination count={pagination.pages} page={page} onChange={(_, p) => setPage(p)} color="primary" shape="rounded" />
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
